import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const KEY = "promptivo.cookieConsent";

export const COOKIE_SETTINGS_EVENT = "open-cookie-settings";

const TABS = [
  { id: "consent", label: "Zgoda" },
  { id: "details", label: "Szczegóły" },
  { id: "about", label: "O plikach cookies" },
];

const CATS = [
  {
    key: "necessary",
    label: "Niezbędne",
    locked: true,
    desc: "Wymagane do działania serwisu — logowanie, bezpieczeństwo i zapamiętanie Twojej zgody. Zawsze aktywne.",
  },
  {
    key: "preferences",
    label: "Preferencje",
    desc: "Zapamiętują Twoje ustawienia i personalizację, aby serwis działał wygodniej.",
  },
  {
    key: "statistics",
    label: "Statystyka",
    desc: "Pomagają anonimowo zrozumieć, jak korzystasz z serwisu, i ulepszać go.",
  },
  {
    key: "marketing",
    label: "Marketing",
    desc: "Służą do dopasowania treści i mierzenia skuteczności działań.",
  },
];

export function CookieConsent() {
  const [show, setShow] = useState(false);
  const [hasStored, setHasStored] = useState(false);
  const [tab, setTab] = useState("consent");
  const [sel, setSel] = useState({ necessary: true, preferences: false, statistics: false, marketing: false });

  useEffect(() => {
    const loadSaved = () => {
      try {
        const s = JSON.parse(localStorage.getItem(KEY) || "null");
        if (s && s.categories) {
          setSel((p) => ({ ...p, ...s.categories, necessary: true }));
          setHasStored(true);
          return true;
        }
      } catch {
        /* ignore */
      }
      return false;
    };
    const stored = loadSaved();
    if (!stored) setShow(true);

    const openSettings = () => {
      loadSaved();
      setTab("consent");
      setShow(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, openSettings);
  }, []);

  const applyAnalytics = (categories) => {
    try {
      if (categories.statistics) window.__promptivoAnalytics?.enable?.();
      else window.__promptivoAnalytics?.disable?.();
    } catch {
      /* ignore */
    }
  };

  const save = (categories, value) => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ value, categories, at: new Date().toISOString() }));
    } catch {
      /* ignore */
    }
    applyAnalytics(categories);
    setHasStored(true);
    setShow(false);
  };

  const rejectAll = () => save({ necessary: true, preferences: false, statistics: false, marketing: false }, "necessary");
  const acceptSelected = () => save(sel, "selected");
  const acceptAll = () => save({ necessary: true, preferences: true, statistics: true, marketing: true }, "all");

  const toggle = (key, locked) => {
    if (locked) return;
    setSel((s) => ({ ...s, [key]: !s[key] }));
  };

  if (!show) return null;

  return (
    <div
      data-testid="cookie-consent-modal"
      className="fixed inset-0 z-[120] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Zgoda na pliki cookie"
    >
      <div className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-panel border border-brand-line bg-brand-surface shadow-2xl sm:max-w-2xl sm:rounded-panel">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6">
          <span className="font-display text-base font-bold tracking-tight text-brand-text">
            PROMPTIVO<span className="text-brand-accent">.</span>
          </span>
          <div className="flex items-center gap-3">
            <Cookie size={20} className="text-brand-accent" aria-hidden />
            {hasStored && (
              <button
                type="button"
                data-testid="cookie-close"
                aria-label="Zamknij ustawienia cookies"
                onClick={() => setShow(false)}
                className="text-brand-sec transition-colors hover:text-brand-text"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-6 border-b border-brand-line px-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`cookie-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 pb-3 text-sm font-semibold transition-colors ${
                tab === t.id
                  ? "border-brand-accent text-brand-text"
                  : "border-transparent text-brand-sec hover:text-brand-text"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content (scrollable) */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tab === "consent" && (
            <div data-testid="cookie-panel-consent">
              <h2 className="font-display text-lg font-semibold text-brand-text">
                Niniejsza strona korzysta z plików cookie
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-brand-sec">
                Wykorzystujemy pliki cookie do działania serwisu, zapamiętania Twoich ustawień oraz — za Twoją zgodą —
                do analityki i dopasowania treści. Możesz zaakceptować wszystkie, wybrać kategorie lub odmówić
                zgody na cookie nieniezbędne. Szczegóły w{" "}
                <Link to="/cookies" className="text-brand-accent hover:underline">
                  Polityce cookies
                </Link>
                .
              </p>
              {/* Przełączniki kategorii */}
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {CATS.map((c) => (
                  <div
                    key={c.key}
                    className="flex flex-col items-start gap-2 rounded-panel border border-brand-line bg-brand-bg px-4 py-3"
                  >
                    <span className="text-sm font-semibold text-brand-text">{c.label}</span>
                    <Switch
                      data-testid={`cookie-toggle-${c.key}`}
                      checked={sel[c.key]}
                      disabled={c.locked}
                      onCheckedChange={() => toggle(c.key, c.locked)}
                      className="data-[state=checked]:bg-brand-accent data-[state=unchecked]:bg-brand-line"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "details" && (
            <div data-testid="cookie-panel-details" className="space-y-4">
              {CATS.map((c) => (
                <div key={c.key} className="flex items-start justify-between gap-4 border-b border-brand-line pb-4 last:border-0 last:pb-0">
                  <div>
                    <p className="text-sm font-semibold text-brand-text">{c.label}</p>
                    <p className="mt-1 text-[13px] leading-relaxed text-brand-sec">{c.desc}</p>
                  </div>
                  <Switch
                    data-testid={`cookie-toggle-details-${c.key}`}
                    checked={sel[c.key]}
                    disabled={c.locked}
                    onCheckedChange={() => toggle(c.key, c.locked)}
                    className="mt-1 shrink-0 data-[state=checked]:bg-brand-accent data-[state=unchecked]:bg-brand-line"
                  />
                </div>
              ))}
            </div>
          )}

          {tab === "about" && (
            <div data-testid="cookie-panel-about">
              <p className="text-sm leading-relaxed text-brand-sec">
                Pliki cookie to niewielkie pliki tekstowe zapisywane na Twoim urządzeniu. Używamy ich, aby serwis
                działał poprawnie, pamiętał Twoje preferencje oraz — za zgodą — mierzył ruch i dopasowywał treści.
                W każdej chwili możesz zmienić decyzję, czyszcząc dane witryny w przeglądarce. Pełne informacje
                znajdziesz w{" "}
                <Link to="/cookies" className="text-brand-accent hover:underline">
                  Polityce cookies
                </Link>
                .
              </p>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-1 gap-2 border-t border-brand-line p-4 sm:grid-cols-3">
          <button
            type="button"
            data-testid="cookie-reject"
            onClick={rejectAll}
            className="rounded-button border border-brand-line px-4 py-3 text-sm font-semibold text-brand-sec transition-colors hover:text-brand-text"
          >
            Odmowa
          </button>
          <button
            type="button"
            data-testid="cookie-accept-selected"
            onClick={acceptSelected}
            className="rounded-button border border-brand-accent px-4 py-3 text-sm font-semibold text-brand-accent transition-colors hover:bg-brand-accent/10"
          >
            Zezwól na wybór
          </button>
          <button
            type="button"
            data-testid="cookie-accept-all"
            onClick={acceptAll}
            className="rounded-button bg-brand-accent px-4 py-3 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
          >
            Zezwól na wszystkie
          </button>
        </div>
      </div>
    </div>
  );
}
